import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { configureRevenueCat, logoutRevenueCat } from '../services/revenuecat';
import { clearDashboardCache } from '../utils/dashboardCache';
import { identify, resetIdentity } from '../lib/analytics';
import { readPendingClaim, clearPendingClaim } from '../lib/tryItStorage';

const API = '/api';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Fix B follow-up (Aug 2026): downstream components (OnboardingOverlay
    // welcome-carousel timer, AskBookmarkInline coach-mark timer) MUST
    // NOT start their reveals until we've resolved whether a pending
    // /try/claim exists and — if so — whether it succeeded. Without this
    // gate, `checkAuth` used to commit a `user` object with
    // `onboarded_via_anon_save=false` while the claim was still
    // in-flight; the overlay would mount and only later `if (suppress)`
    // would hide it (mount-then-hide, exactly what the spec forbids).
    // Flips `true` in one of three terminal states:
    //   1. auth resolved with no pending claim
    //   2. auth resolved AND claim POST returned (success or 4xx/network)
    //   3. auth failed / unauthenticated
    const [authBootstrapReady, setAuthBootstrapReady] = useState(false);
    // Toggled ON the instant the native Google bridge returns a valid
    // id_token, OFF once `setUser` fires (or the flow errors out). This
    // masks the ~1 s stutter between the picker dismissing and the
    // POST /auth/google/native round-trip completing — otherwise the
    // user stares at the previous modal for the whole network hop.
    const [oauthInFlight, setOauthInFlight] = useState(false);

    // Belt-and-braces: even if some future code path forgets to clear
    // the flag, auto-clear after 20s so the overlay never permanently
    // strands a user (the fix that motivated this: TWA Aug 23 2026).
    useEffect(() => {
        if (!oauthInFlight) return;
        const t = setTimeout(() => setOauthInFlight(false), 20000);
        return () => clearTimeout(t);
    }, [oauthInFlight]);
    const retryCountRef = useRef(0);

    // Try-it → real-account handoff. Extracted so both auth paths
    // (session-restore `checkAuth` and native Android `google/native`)
    // share one race-free sequence: run the claim BEFORE `setUser`
    // and optimistically merge `onboarded_via_anon_save: true` into
    // the user object that gets committed. This way the very first
    // React commit already carries the correct flag — OnboardingOverlay
    // and AskBookmarkInline observe `suppress=true` on their first
    // read, no mount-then-hide.
    //
    // Contract:
    //   - No pending claim in localStorage → returns userData unchanged.
    //   - Claim POST returns 2xx → returns {...userData, onboarded_via_anon_save: true}
    //     and clears the pending payload + dashboard cache.
    //   - Claim POST returns 404 (bookmark already claimed / expired)
    //     → clears pending payload, returns userData unchanged (no
    //     retroactive flag flip — matches server truth).
    //   - Any other status / network error → returns userData unchanged
    //     so the user still lands in a working session; the pending
    //     payload stays in localStorage and will be retried next auth.
    const runPendingClaim = useCallback(async (userData) => {
        try {
            const pending = readPendingClaim();
            if (!pending?.bookmark_id || !pending?.device_id) return userData;
            const claimRes = await fetch(`${API}/try/claim`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(pending),
            });
            if (claimRes.ok) {
                clearPendingClaim();
                clearDashboardCache();
                return { ...userData, onboarded_via_anon_save: true };
            }
            if (claimRes.status === 404) {
                clearPendingClaim();
                return userData;
            }
            return userData;
        } catch (_) {
            return userData;
        }
    }, []);

    // One entry point every non-checkAuth auth path uses to hand off
    // to React. Guarantees the race-free sequence: claim → setUser →
    // ready flag → background /auth/me refresh. Exposed via context
    // so `AuthCallback.js` (web OAuth) can hit exactly the same path
    // as the native Android bridge and the cookie session-restore.
    const commitAuthenticatedUser = useCallback(async (rawUserData) => {
        const userData = await runPendingClaim(rawUserData);
        setUser(userData);
        setAuthBootstrapReady(true);
        if (userData.onboarded_via_anon_save && !rawUserData.onboarded_via_anon_save) {
            (async () => {
                try {
                    const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
                    if (r.ok) {
                        const fresh = await r.json();
                        setUser((prev) => ({ ...prev, ...fresh }));
                    }
                } catch (_) { /* best-effort refresh */ }
            })();
        }
        return userData;
    }, [runPendingClaim]);

    const checkAuth = useCallback(async (options = {}) => {
        const { silent = false, maxRetries = 1, timeoutMs = 4000 } = options;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            // Abort each attempt after `timeoutMs` so a Cloudflare / cold-start
            // hang can never freeze the splash screen for 15+ seconds.
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);
            try {
                const response = await fetch(`${API}/auth/me`, {
                    credentials: 'include',
                    signal: controller.signal,
                });
                clearTimeout(timer);

                if (response.ok) {
                    const rawUserData = await response.json();
                    // Fix B follow-up (Aug 2026): run any pending
                    // try-it claim BEFORE the first setUser so the
                    // committed user object already carries
                    // `onboarded_via_anon_save: true`. Prevents the
                    // OnboardingOverlay / AskBookmarkInline coach-mark
                    // race documented in PRD.md.
                    const userData = await runPendingClaim(rawUserData);
                    setUser(userData);
                    retryCountRef.current = 0;
                    // If the claim flipped the flag, refresh from
                    // /auth/me in the background so any other
                    // server-side mutations (bookmark_count, tier, …)
                    // land too. Fire-and-forget — the current commit
                    // is already correct for suppression purposes.
                    if (userData.onboarded_via_anon_save && !rawUserData.onboarded_via_anon_save) {
                        (async () => {
                            try {
                                const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
                                if (r.ok) {
                                    const fresh = await r.json();
                                    setUser((prev) => ({ ...prev, ...fresh }));
                                }
                            } catch (_) { /* best-effort refresh */ }
                        })();
                    }
                    // Identify the user in GA4 with the `app_surface`
                    // user property so every event that follows
                    // (sign_up, login, first_save, first_ai_question, …)
                    // is auto-tagged with `twa` vs `web` — no per-event
                    // wiring required. `document.referrer` starts with
                    // `android-app://` when Chrome hands the URL to a
                    // Trusted Web Activity; the standalone display-mode
                    // check covers PWA installs on any platform. The
                    // install-registration side-effect is in the
                    // `useEffect(..., [user])` below so it also fires
                    // for the native-Android sign-in path which sets
                    // user directly without re-running checkAuth().
                    // Admin impersonation (view-only QA): don't attribute
                    // analytics / RevenueCat to the impersonated user.
                    if (userData.impersonation) {
                        if (!silent) setLoading(false);
                        setAuthBootstrapReady(true);
                        return userData;
                    }
                    try {
                        const isTwa =
                            (typeof window !== 'undefined' && (
                                sessionStorage.getItem('bs_is_twa') === '1'
                                || window.matchMedia('(display-mode: standalone)').matches
                                || document.referrer.includes('android-app://')
                            ));
                        identify(userData.user_id, {
                            app_surface: isTwa ? 'twa' : 'web',
                        });
                        // Microsoft Clarity — same identify + tag pattern
                        // as GA4 so recordings can be filtered by
                        // plan_tier and cross-referenced with the user_id
                        // shown in the admin dashboard. `identify()` is
                        // Clarity's user-scoping API — it links future
                        // events on this session back to this user_id.
                        // `set()` adds tags visible in the recording
                        // filter panel. All 3 are no-ops if Clarity
                        // didn't load (guard flag from index.html).
                        if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
                            window.clarity('identify', userData.user_id, undefined, undefined, userData.name || undefined);
                            window.clarity('set', 'plan_tier', userData.tier || userData.subscription_tier || 'free');
                            window.clarity('set', 'surface', isTwa ? 'twa' : 'web');
                        }
                    } catch (_) { /* never break auth on analytics */ }
                    // Identify the user with RevenueCat so subsequent paywall
                    // / status calls map to the correct app_user_id. Errors
                    // are swallowed inside configureRevenueCat itself.
                    configureRevenueCat(userData.user_id, {
                        $email: userData.email,
                        $displayName: userData.name || userData.email,
                    });
                    if (!silent) setLoading(false);
                    setAuthBootstrapReady(true);
                    return userData;
                }

                if (response.status === 401 && attempt < maxRetries) {
                    await new Promise(r => setTimeout(r, 600));
                    continue;
                }

                if (!silent) setUser(null);
            } catch (error) {
                clearTimeout(timer);
                const reason = error?.name === 'AbortError' ? 'timeout' : (error?.message || error);
                console.error(`Auth check failed (attempt ${attempt + 1}):`, reason);
                if (attempt < maxRetries) {
                    await new Promise(r => setTimeout(r, 600));
                    continue;
                }
                if (!silent && !user) setUser(null);
            }
        }
        if (!silent) setLoading(false);
        // Auth resolved (unauthenticated / errored). Downstream timers
        // may proceed — there's no pending claim to wait on for an
        // unauthenticated user.
        setAuthBootstrapReady(true);
        return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Skip auth check if on the OAuth callback route
        const params = new URLSearchParams(window.location.search);
        if (params.has('code') && window.location.pathname === '/auth/google') {
            setLoading(false);
            // We're mid-OAuth handoff; the callback page owns bootstrap.
            // Flip ready so any transiently-mounted overlay won't wait
            // indefinitely if the user pops back to a suppressed screen.
            setAuthBootstrapReady(true);
            return;
        }
        if (params.has('error') && window.location.pathname === '/auth/google') {
            setLoading(false);
            setAuthBootstrapReady(true);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    // ── App install registration ────────────────────────────────
    // Fires POST /api/analytics/install the first time we see this
    // user on a native shell surface (Android TWA / iOS wrapper /
    // installed PWA). Keyed on `user` so it works for BOTH auth
    // paths:
    //   1. Cookie-authed cold-start   →  checkAuth sets user
    //   2. First-time native Google sign-in  →  setUser called
    //      directly from the brainsync-google-signin-result handler
    //      (which does NOT re-run checkAuth, so previously the
    //      install-reg silently no-op'd for first-time TWA signups).
    // Backend dedupes on (user_id, surface) so re-opens don't inflate
    // the count. Client-side sessionStorage guard prevents a burst of
    // requests during a single browser session.
    useEffect(() => {
        if (!user?.user_id || user.impersonation || typeof window === 'undefined') return;
        try {
            let surface = null;
            const isTwa = (
                sessionStorage.getItem('bs_is_twa') === '1'
                || window.matchMedia?.('(display-mode: standalone)').matches
                || document.referrer.includes('android-app://')
            );
            if (isTwa) surface = 'twa';
            else if (window.navigator?.standalone === true) surface = 'ios_pwa';
            else if (window.matchMedia?.('(display-mode: standalone)').matches) surface = 'web_pwa';
            if (!surface) return;  // regular browser tab — nothing to record

            const ssnKey = `bs_install_reg:${user.user_id}:${surface}`;
            if (sessionStorage.getItem(ssnKey)) return;
            sessionStorage.setItem(ssnKey, '1');

            // Attach the persistent device_id so backend can UPGRADE
            // the anonymous install row (written by install-tracker.js
            // at page-load time) in place, attaching user_id — avoids
            // counting the same physical install as two rows (one
            // anonymous, one signed-in).
            let deviceId = null;
            try { deviceId = localStorage.getItem('bs_device_id'); } catch (_) { /* private mode */ }

            fetch(`${API}/analytics/install`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({ surface, device_id: deviceId || undefined }),
            }).catch(() => { /* install-reg failures shouldn't break auth */ });
        } catch (_) { /* never break auth on analytics */ }
    }, [user]);

    // ── Firebase app instance ID → user record ──────────────────
    // Android only. The native shell caches the Firebase app instance
    // ID and exposes it on BrainSyncNative; we store it against the
    // user so a Google Ads `first_open` can be joined back to an
    // account later. Backend is write-once, so this doubles as "set it
    // at signup" and "backfill it at login if the field is empty".
    //
    // NO analytics event is sent with this — it is an identifier
    // handoff, nothing else.
    useEffect(() => {
        if (!user?.user_id || user.impersonation || typeof window === 'undefined') return;
        const bridge = window.BrainSyncNative;
        if (!bridge || typeof bridge.getFirebaseInstanceId !== 'function') return;

        const ssnKey = `bs_fb_iid:${user.user_id}`;
        let done = false;

        const send = (id) => {
            if (done || !id) return;
            done = true;
            try {
                if (sessionStorage.getItem(ssnKey)) return;
                sessionStorage.setItem(ssnKey, '1');
            } catch (_) { /* private mode — fall through, backend is idempotent */ }
            fetch(`${API}/analytics/firebase-instance-id`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({ app_instance_id: id }),
            }).catch(() => { /* never break auth on analytics */ });
        };

        try {
            const cached = bridge.getFirebaseInstanceId();
            if (cached) {
                send(cached);
                return;
            }
        } catch (_) { /* bridge threw — fall through to the hook */ }

        // First launch: the native fetch hasn't resolved yet, so the shell
        // calls this hook when it does instead of us polling.
        const prev = window.onBrainSyncFirebaseId;
        window.onBrainSyncFirebaseId = (id) => {
            try { if (typeof prev === 'function') prev(id); } catch (_) { /* ignore */ }
            send(id);
        };
        return () => {
            if (window.onBrainSyncFirebaseId && prev !== undefined) {
                window.onBrainSyncFirebaseId = prev;
            }
        };
    }, [user]);

    const login = (options = {}) => {
        // Web OAuth fallback factored out so the native-bridge failure path
        // can call it without duplicating the URL building.
        const webOAuth = () => {
            const redirectUri = window.location.origin + '/auth/google';
            const scope = 'openid email profile';
            const savedEmail = localStorage.getItem('brainsync_user_email');
            let prompt = options.prompt || 'select_account';
            let extras = '';
            if (savedEmail) {
                extras += `&login_hint=${encodeURIComponent(savedEmail)}`;
            }
            const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&prompt=${prompt}${extras}`;
            window.location.href = googleAuthUrl;
        };

        // TWA deep-link path - if the BrainSync TWA is installed and we're
        // sitting in mobile Chrome (i.e. NOT inside the TWA itself), starting
        // a web OAuth flow is destined to fail: Android App Links will
        // intercept Google's redirect back to brainsync.pro/auth/google and
        // hand the URL to the installed app, leaving the Chrome tab stuck
        // on Google's "Choose an account" loader. Instead, deep-link
        // straight into the TWA via an Android intent URI - Chrome navigates
        // away cleanly, the TWA handles auth natively, and there's no
        // orphaned spinning tab.
        try {
            const isStandalone =
                window.matchMedia('(display-mode: standalone)').matches
                || window.navigator.standalone === true
                || document.referrer.includes('android-app://');
            if (!isStandalone
                && !options.forceWebOAuth
                && typeof navigator.getInstalledRelatedApps === 'function') {
                navigator.getInstalledRelatedApps().then((apps) => {
                    // Match either the Play Store package (com.brainsync.myapp)
                    // or the legacy sideloaded APK package (com.brainsync.app).
                    const installed = Array.isArray(apps) && apps.find(
                        (a) => a && a.platform === 'play'
                            && (a.id === 'com.brainsync.myapp' || a.id === 'com.brainsync.app'),
                    );
                    if (installed) {
                        window.location.href =
                            `intent://brainsync.pro/dashboard#Intent;scheme=https;package=${installed.id};end`;
                        return;
                    }
                    // No TWA installed → continue with native bridge or web OAuth below.
                    proceedWithOAuth();
                }).catch(() => {
                    proceedWithOAuth();
                });
                return;
            }
        } catch (_) { /* fall through to normal OAuth */ }

        proceedWithOAuth();

        function proceedWithOAuth() {

        // Native path - TWA / future RN clients expose a BrainSyncAuth bridge
        // that triggers the system Google account picker and returns an ID
        // token directly. No browser tab, no leftover Chrome session.
        if (typeof window !== 'undefined' && window.BrainSyncAuth?.signIn) {
            // Cover the WebView the instant the user taps "Continue
            // with Google" — before the native picker even opens.
            // Otherwise, on TWA there's a visible tick between the
            // native picker closing and `setOauthInFlight(true)`
            // firing inside `onResult` where the caller's sheet
            // remains on top of the bookmark page. Flashing the veil
            // eagerly closes that window. (Reported TWA Aug 27 2026.)
            setOauthInFlight(true);
            // Tracks whether the native bridge already produced a result so
            // the safety-net timer below does NOT double-fire web OAuth on
            // top of a successful native sign-in.
            let resultReceived = false;
            const onResult = async (event) => {
                if (resultReceived) return; // already handled
                resultReceived = true;
                window.removeEventListener('brainsync-google-signin-result', onResult);
                const { ok, id_token, error } = event.detail || {};
                if (!ok || !id_token) {
                    // Native sign-in failed mid-flight (e.g. Google Cloud
                    // Console OAuth client doesn't have this package+cert
                    // registered, user cancelled, no Google account on
                    // device, etc.). Fall back to the web OAuth flow so the
                    // user can still sign in. Without this fallback, the
                    // user just bounces back to the landing page silently
                    // and has no way to log in.
                    console.warn('Native Google Sign-In failed, falling back to web OAuth:', error);
                    setOauthInFlight(false);
                    webOAuth();
                    return;
                }
                // Veil already up from `login()` — keep it up through
                // the /auth/google/native round-trip.
                // Veil already up from `login()` — keep it up through
                // the /auth/google/native round-trip.
                // Pass the referral tag through, same as the web flow.
                let ref = null;
                try { ref = localStorage.getItem('brainsync_ref') || null; } catch (_) {}
                try {
                    const res = await fetch(`${API}/auth/google/native`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ id_token, ref }),
                    });
                    if (res.ok) {
                        const rawUserData = await res.json();
                        try { localStorage.setItem('brainsync_user_email', rawUserData.email || ''); } catch (_) {}
                        try { localStorage.removeItem('brainsync_ref'); } catch (_) {}
                        // Fix B follow-up (Aug 2026): run the pending
                        // try-it claim BEFORE `setUser` and merge
                        // `onboarded_via_anon_save: true` into the
                        // committed user object so OnboardingOverlay /
                        // AskBookmarkInline observe `suppress=true` on
                        // their very first read. Shared with checkAuth
                        // via `runPendingClaim`.
                        const userData = await runPendingClaim(rawUserData);
                        // IMPORTANT: dismiss the veil BEFORE calling
                        // setUser. Without this the "Signing you in…"
                        // overlay stays fixed at z-index 10000 forever
                        // on TWA — the WebView doesn't unmount the
                        // provider tree the way desktop Chrome
                        // sometimes does on setUser, so the overlay
                        // never re-renders itself out. (Reported via
                        // screenshot on TWA Aug 23 2026 — user was
                        // stuck on "Signing you in…" until they
                        // relaunched the app.)
                        setOauthInFlight(false);
                        setUser(userData);
                        setAuthBootstrapReady(true);
                        // Background /auth/me refresh if the claim
                        // flipped the flag — pulls any other server
                        // fields (bookmark_count, tier, …) that the
                        // claim mutated.
                        if (userData.onboarded_via_anon_save && !rawUserData.onboarded_via_anon_save) {
                            (async () => {
                                try {
                                    const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
                                    if (r.ok) {
                                        const fresh = await r.json();
                                        setUser((prev) => ({ ...prev, ...fresh }));
                                    }
                                } catch (_) { /* best-effort refresh */ }
                            })();
                        }
                        if (userData.user_id) configureRevenueCat(userData.user_id, {
                            $email: userData.email,
                            $displayName: userData.name || userData.email,
                        });
                    } else {
                        // Server rejected the id_token (likely an audience
                        // mismatch - Google Cloud Console hasn't been
                        // updated to recognise this Android client). Fall
                        // back to web OAuth instead of leaving the user
                        // stuck on the landing page.
                        console.error('Native Google Sign-In server rejected token:', res.status);
                        setOauthInFlight(false);
                        webOAuth();
                    }
                } catch (err) {
                    console.error('Native Google Sign-In server call failed:', err);
                    setOauthInFlight(false);
                    webOAuth();
                }
            };
            window.addEventListener('brainsync-google-signin-result', onResult);
            try {
                window.BrainSyncAuth.signIn();
                // Safety net: only fall back to web OAuth if (a) no result
                // event has fired AND (b) the native picker doesn't appear
                // to be on-screen (document is visible). A user staring at
                // the picker keeps document.hidden=true, so the timer
                // simply no-ops - the listener stays alive and the result
                // event will be handled whenever the user actually picks
                // an account. This eliminates the "click email → also see
                // Chrome Google login" double-prompt that happens when
                // both flows fire.
                setTimeout(() => {
                    if (resultReceived) return;
                    if (document.hidden) return; // native picker is up - keep waiting
                    window.removeEventListener('brainsync-google-signin-result', onResult);
                    console.warn('Native Google bridge produced no result and picker is not visible - falling back to web OAuth');
                    // If the picker is dismissed without a result the veil
                    // needs to come down before the webOAuth navigation
                    // (otherwise the user briefly sees the veil on top of
                    // the landing that Chrome renders during redirect).
                    setOauthInFlight(false);
                    webOAuth();
                }, 4000);
                return;
            } catch (e) {
                window.removeEventListener('brainsync-google-signin-result', onResult);
                setOauthInFlight(false);
                console.warn('BrainSyncAuth.signIn failed, falling back to web OAuth:', e);
            }
        }

        webOAuth();
        } // end proceedWithOAuth
    };

    // Try silent sign-in (no UI) for returning users
    const trySilentSignIn = useCallback(() => {
        const savedEmail = localStorage.getItem('brainsync_user_email');
        if (!savedEmail) return false;

        const redirectUri = window.location.origin + '/auth/google';
        const scope = 'openid email profile';
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&prompt=none&login_hint=${encodeURIComponent(savedEmail)}`;
        window.location.href = googleAuthUrl;
        return true;
    }, []);

    const logout = async () => {
        try {
            await fetch(`${API}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            localStorage.removeItem('brainsync_user_email');
            // Wipe the persistent dashboard snapshot so the next account on
            // this device never sees the previous user's bookmarks.
            clearDashboardCache();
            // Clear RevenueCat identification so the next user starts clean
            logoutRevenueCat();
            // Clear GA4 user_id so the same browser doesn't attribute
            // the next account's events to the previous user.
            resetIdentity();
        }
    };

    const value = {
        user,
        setUser,
        commitAuthenticatedUser,
        authBootstrapReady,
        loading,
        login,
        logout,
        checkAuth,
        trySilentSignIn
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            {oauthInFlight && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: '#FDFCF8',
                        zIndex: 10000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    }}
                    data-testid="oauth-in-flight-veil"
                >
                    <div style={{
                        width: 76, height: 76, borderRadius: 20,
                        background: '#0F172A',
                        display: 'grid', placeItems: 'center',
                        boxShadow: '0 12px 32px rgba(15,23,42,.18)',
                        animation: 'bsPulse 1.4s ease-in-out infinite',
                    }}>
                        <img src="/icon-192.png" alt="" style={{ width: 76, height: 76, borderRadius: 20, display: 'block' }} />
                    </div>
                    <p style={{ marginTop: 22, fontSize: 15, fontWeight: 600, color: '#24423A' }}>Signing you in…</p>
                    <style>{`@keyframes bsPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}`}</style>
                </div>
            )}
        </AuthContext.Provider>
    );
};
